import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { getHeaderHtml, parseHeader, drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { themeColors } from "../../../util/themeColors";
import i18n from "../../../i18n";

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

export const printBillStatementReceipt = (
  patientData,
  serviceItems = [],
  totalBill,
  amountReceived,
  outstandingAmount,
  billingDate,
  paymentType,
  txnNo,
  authCtx,
  billId
) => {
  const hospitalData = authCtx?.hospitalData || authCtx || {};
  const { hospitalName } = hospitalData;
  const t = i18n.t;

  const printWindow = window.open("", "_blank", "width=900,height=650");
  if (!printWindow) return;

  const calculatedTotal =
    totalBill && totalBill > 0
      ? totalBill
      : serviceItems.reduce((sum, item) => sum + (item.netAmount || 0), 0);

  printWindow.document.write(`
    <html>
      <head>
        <title>${t("Bill Receipt")}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0px; }
          .header { text-align: center; margin-bottom: 10px; }
          .patient-info { display: flex; justify-content: space-between; margin: 10px 0; font-size: 14px; }
          .patient-info .left, .patient-info .right { width: 48%; }
          .patient-info .left { text-align:left; }
          .patient-info .right { text-align:right; }
          .patient-info p { margin: 4px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #333; padding: 6px; text-align: left; font-size: 13px; }
          th { background: #eeeeee !important; color: #000000 !important; font-weight: bold; }
          .total { text-align: right; font-weight: bold; }
          .amount-summary { display:flex; justify-content: space-between; margin-top: 10px; font-size:14px; }
          .amount-summary .left, .amount-summary .right { width:48%; }
          .payment-summary { display:flex; justify-content: space-between; margin-top: 2px; font-size:14px; }
          .payment-summary .left, .amount-summary .right { width:48%; }
          
          /* Footer Signature positioning */
          .footer-sign {
            position: fixed;
            bottom: 30px;
            right: 30px;
            width: 220px;
            text-align: center;
          }
          @media screen {
            .footer-sign {
              position: relative;
              margin-top: 80px;
              float: right;
            }
          }
        </style>
      </head>
      <body>
        ${getHeaderHtml(hospitalData)}
        <div class="patient-info">
          <div class="left">
            <p><strong>${t("Name")}:</strong> ${patientData?.firstName || ""} ${patientData?.lastName || ""}</p>
            <p><strong>${t("Address")}:</strong> ${patientData?.address || "-"}</p>
          </div>
          <div class="right">
            <p><strong>${t("Bill No")}:</strong> ${billId || "-"}</p>
            <p><strong>${t("Mobile")}:</strong> ${patientData?.mobile || "-"}</p>
            <p><strong>${t("Date")}:</strong> ${billingDate ? new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "-"}</p>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>${t("Sr No")}</th>
              <th>${t("Expense")}</th>
                <th>${t("Amount")}</th>
                <th>${t("Quantity")}</th>
              <th>${t("Total Amt")}</th>
               <th>${t("Discount(%)")}</th>
               <th>${t("Net Amt")}</th>
            </tr>
          </thead>
          <tbody>
            ${serviceItems && serviceItems.length > 0
      ? serviceItems
        .map(
          (item, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${item.serviceDescription || "-"}</td>
                  <td>${item.actualAmount ?? 0}</td>
                  <td>${item.quantity ?? 1}</td>
                  <td>${((item.actualAmount ?? 0) * (item.quantity ?? 1)).toFixed(2)}</td>
                  <td>${item.discount ?? 0}</td>
                  <td>${item.netAmount ?? 0}</td>
                </tr>`
        )
        .join("")
      : `<tr><td colspan="7" style="text-align:center;">${t("No services found")}</td></tr>`
    }
            <tr>
              <td colspan="6" class="total">${t("Total")}</td>
              <td><strong>${calculatedTotal.toFixed(2)}</strong></td>
            </tr>
          </tbody>
        </table>
        <div class="amount-summary">
          <div class="left">
            <p><strong>${t("Amount Received")}:</strong> ${amountReceived || 0}</p>
          </div>
          <div class="right">
            <p><strong>${t("Outstanding Amount")}:</strong> ${outstandingAmount || 0}</p>
          </div>
        </div>
          <div class="payment-summary">
          <div class="left">
             ${paymentType
      ? `<p><strong>${t("Payment Type")}:</strong> ${paymentType || "-"}</p>`
      : ""
    }
          </div>
          <div class="right">
            ${paymentType && paymentType.toLowerCase() !== "cash"
      ? `<p><strong>${t("Txn No")}:</strong> ${txnNo || "-"}</p>`
      : ""
    }
          </div>
        </div>

        <!-- Signature Section -->
        <div class="footer-sign">
          <div style="border-top: 1.5px solid #000; margin-bottom: 5px;"></div>
          <p style="margin: 0; font-weight: bold; font-size: 14px;">${t("Authority Sign / Doctor Sign")}</p>
          ${(() => {
      const userRole = authCtx?.role;
      const userName = authCtx?.userName;
      if (userRole === "ROLE_DOCTOR" && userName) {
        return `<p style="margin: 2px 0 0; font-size: 12px; font-weight: bold;">${userName}</p>`;
      }
      return "";
    })()}
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  printWindow.close();
};

const buildPDF = (doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData || authCtx || {};
  const t = i18n.t;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const startY = drawReportHeader(doc, hospitalData, "Bill Receipt", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

  let currentY = startY + 5;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.text(
    `Name: ${patientData?.firstName || ""} ${patientData?.lastName || ""}`,
    14,
    currentY
  );
  const dateStr = billingDate ? new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "-";
  doc.text(`Date: ${dateStr}`, pageWidth - 75, currentY);

  currentY += 7;
  doc.text(`Bill No: ${billId || "-"}`, 14, currentY);
  doc.text(`Mobile: ${patientData?.mobile || "-"}`, pageWidth - 75, currentY);

  currentY += 7;
  doc.text(
    `Address: ${patientData?.address || "-"}`,
    14,
    currentY
  );

  currentY += 8;
  const tableData = serviceItems.map((item, index) => [
    index + 1,
    item.serviceDescription || "-",
    item.actualAmount ?? 0,
    item.quantity ?? 1,
    ((item.actualAmount ?? 0) * (item.quantity ?? 1)).toFixed(2),
    item.discount ?? 0,
    item.netAmount ?? 0
  ]);

  doc.autoTable({
    startY: currentY,
    head: [
      ["Sr No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
    ],
    body: tableData,
    foot: [
      ["", "", "", "", "", "Total", parseFloat(totalBill || 0).toFixed(2)]
    ],
    theme: "grid",
    styles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "center",
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    },
    bodyStyles: { fillColor: [255, 255, 255] },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "right",
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    }
  });

  const finalY = doc.lastAutoTable.finalY + 10;
  doc.text(`Amount Received: ${amountReceived || 0}`, 14, finalY);
  doc.text(`Outstanding Amount: ${outstandingAmount || 0}`, pageWidth - 70, finalY);

  let nextY = finalY + 8;
  doc.text(`Payment Type: ${paymentType || "-"}`, 14, nextY);
  if (paymentType && paymentType.toLowerCase() !== "cash") {
    doc.text(`Txn No: ${txnNo || "-"}`, pageWidth - 70, nextY);
  }

  drawAuthorizedSignatory(doc, hospitalData);

  const userName = authCtx?.userName;
  if (authCtx?.role === "ROLE_DOCTOR" && userName) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`( ${userName} )`, pageWidth - 45, pageHeight - 10, { align: "center" });
  }
};

const buildPDFA5 = (doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData || authCtx || {};
  const t = i18n.t;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const startY = drawReportHeader(doc, hospitalData, "Bill Receipt", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

  let currentY = startY + 5;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.text(
    `Name: ${patientData?.firstName || ""} ${patientData?.lastName || ""}`,
    14,
    currentY
  );
  const dateStr = billingDate ? new Date(billingDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : "-";
  doc.text(`Date: ${dateStr}`, pageWidth - 60, currentY);

  currentY += 6;
  doc.text(`Bill No: ${billId || "-"}`, 14, currentY);
  doc.text(`Mobile: ${patientData?.mobile || "-"}`, pageWidth - 60, currentY);

  currentY += 6;
  doc.text(
    `Address: ${patientData?.address || "-"}`,
    14,
    currentY
  );

  currentY += 8;
  const tableData = serviceItems.map((item, index) => [
    index + 1,
    item.serviceDescription || "-",
    item.actualAmount ?? 0,
    item.quantity ?? 1,
    ((item.actualAmount ?? 0) * (item.quantity ?? 1)).toFixed(2),
    item.discount ?? 0,
    item.netAmount ?? 0
  ]);

  doc.autoTable({
    startY: currentY,
    head: [
      ["Sr No", "Expense", "Amount", "Quantity", "Total Amt", "Discount(%)", "Net Amt"]
    ],
    body: tableData,
    foot: [
      ["", "", "", "", "", "Total", parseFloat(totalBill || 0).toFixed(2)]
    ],
    theme: "grid",
    styles: {
      fontSize: 7,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "center",
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    },
    bodyStyles: { fillColor: [255, 255, 255] },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      halign: "right",
      lineWidth: 0.1,
      lineColor: [0, 0, 0]
    }
  });

  const finalY = doc.lastAutoTable.finalY + 8;
  doc.text(`Amount Received: ${amountReceived || 0}`, 14, finalY);
  doc.text(`Outstanding Amount: ${outstandingAmount || 0}`, pageWidth - 60, finalY);

  let nextY = finalY + 6;
  doc.text(`Payment Type: ${paymentType || "-"}`, 14, nextY);
  if (paymentType && paymentType.toLowerCase() !== "cash") {
    doc.text(`Txn No: ${txnNo || "-"}`, pageWidth - 60, nextY);
  }

  drawAuthorizedSignatory(doc, hospitalData);

  const userName = authCtx?.userName;
  if (authCtx?.role === "ROLE_DOCTOR" && userName) {
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`( ${userName} )`, pageWidth - 35, pageHeight - 8, { align: "center" });
  }
};

export const exportBillPDF = (patientData, serviceItems = [], totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, format = "a4", authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: format === "a5" ? "a5" : "a4",
  });

  if (format === "a5") {
    buildPDFA5(doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId);
  } else {
    buildPDF(doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId);
  }

  const patientName = `${patientData?.firstName || "Patient"}_${patientData?.lastName || ""}`.trim();
  const safeName = patientName.replace(/\s+/g, "_") || "patient";
  doc.save(`${safeName}_bill.pdf`);
};

export const shareBillViaWhatsApp = async (patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId) => {
  const hospitalData = authCtx?.hospitalData;
  const t = i18n.t;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });
  buildPDF(doc, patientData, serviceItems, totalBill, amountReceived, outstandingAmount, billingDate, paymentType, txnNo, authCtx, billId);

  const pdfBlob = doc.output("blob");
  const patientMobile = patientData?.mobile || "";
  const patientName = `${patientData?.firstName || ""} ${patientData?.lastName || ""}`.trim() || t("Patient");
  const cleanMobile = patientMobile.replace(/\D/g, '');
  const hospitalName = hospitalData?.hospitalName || t("the hospital");

  // Format items for WhatsApp message
  const itemDetails = serviceItems && serviceItems.length > 0
    ? serviceItems.map((item, index) => {
      const srNo = index + 1;
      const desc = item.serviceDescription || "-";
      const actualAmount = parseFloat(item.actualAmount || 0).toFixed(2);
      const qty = item.quantity || 1;
      const totalAmt = (parseFloat(actualAmount) * parseFloat(qty)).toFixed(2);
      const discount = item.discount || 0;
      const netAmt = (item.netAmount ?? ((item.actualAmount || 0) * qty)).toFixed(2);

      return `${srNo} | ${desc} | ${actualAmount} | ${qty} | ${totalAmt} | ${discount} | ${netAmt}`;
    }).join('\n')
    : t("No items");

  const total = parseFloat(totalBill || 0).toFixed(2);

  const message = `${t("Hello")} ${patientName}, ${t("your bill receipt from")} ${hospitalName} ${t("is ready")}.
*${t("Bill No")}:* ${billId || "-"}

*${t("Sr No")} | ${t("Expense")} | ${t("Amount")} | ${t("Quantity")} | ${t("Total Amt")} | ${t("Discount(%)")} | ${t("Net Amt")}*
${itemDetails}
--------------------------------
*${t("Total")} | ${total}*

${t("regards")}
${hospitalName}`;

  // 📥 On Desktop: Always trigger download so it's ready to drag-and-drop into WhatsApp Web
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile) {
    doc.save(`${patientName}_Bill.pdf`);
  }

  // Priority: Open WhatsApp directly (Web or App)
  const waUrl = `https://wa.me/91${cleanMobile}?text=${encodeURIComponent(message)}`;
  window.open(waUrl, "_blank");

  // Optional: Native share only for mobile
  if (isMobile && navigator.share && navigator.canShare) {
    const file = new File([pdfBlob], `${patientName}_Bill.pdf`, { type: "application/pdf" });
    if (navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: t("Bill Receipt"),
          text: message,
          // note: some platforms don't support sharing files + text together well
        });
      } catch (error) {
        console.log("Native share cancelled");
      }
    }
  }
};
